import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { qualityAPI } from '../../lib/api';
import type { NCRFormData, RepeatInfo } from '../../components/quality/ncr-create/ncrFormTypes';

/**
 * CR-8716: Repeat Issue on the Create NCR form.
 *
 * Looks up whether this part number + fault group was raised before and
 * pre-fills the form's Yes/No with the answer. Once the raiser picks a value
 * themselves (`markChosen`) the lookup still runs — the form shows it as a
 * suggestion — but never overwrites their choice.
 *
 * @param partNumber - the form's part number
 * @param faultGroup - the form's fault group id, or null
 * @param setFormData - the Create NCR form's state setter
 * @returns the latest lookup (null until both fields are filled) and
 *   `markChosen`, to call when the raiser sets the value by hand
 */
export function useRepeatIssue(
  partNumber: string,
  faultGroup: number | null,
  setFormData: Dispatch<SetStateAction<NCRFormData>>,
) {
  const [repeatInfo, setRepeatInfo] = useState<RepeatInfo | null>(null);
  const chosen = useRef(false);

  useEffect(() => {
    const part = partNumber.trim();
    if (!part || !faultGroup) {
      setRepeatInfo(null);
      // Nothing to match on any more, so an earlier suggested "Yes" no longer holds.
      if (!chosen.current) setFormData(prev => (prev.is_repeat_issue ? { ...prev, is_repeat_issue: false } : prev));
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      qualityAPI.ncrRepeatCheck({ part_number: part, fault_group: faultGroup })
        .then(res => {
          // A slower answer for an older part / fault group must not land.
          if (!live) return;
          setRepeatInfo(res.data);
          if (!chosen.current) setFormData(prev => ({ ...prev, is_repeat_issue: res.data.is_repeat }));
        })
        .catch(() => { if (live) setRepeatInfo(null); });
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [partNumber, faultGroup, setFormData]);

  const markChosen = useCallback(() => { chosen.current = true; }, []);

  return { repeatInfo, markChosen };
}
